<template>
  <Card>
    <CardHeader>
      <CardTitle class="tw-flex tw-items-center tw-gap-2">
        <div class="tw-flex tw-items-center tw-justify-between tw-w-full">
          <div class="tw-flex tw-items-center tw-gap-2">
            <Users class="tw-w-5 tw-h-5 tw-text-secondary-500" />
            <span class="tw-font-bold tw-text-[16px] tw-text-secondary-500 tw-m-0">
              {{ t('customer_management.assign_team.title') }}
            </span>
          </div>
          <Button type="button" variant="ghost" @click="showInviteDialog = true" class="tw-gap-2">
            <UserPlus class="tw-w-4 tw-h-4" />
            {{ t('customer_management.titles.invite_user') }}
          </Button>
        </div>
      </CardTitle>
    </CardHeader>

    <CardContent class="tw-p-6">
      <div class="tw-space-y-0">
        <!-- Purchasers Section -->
        <div class="tw-pb-6 tw-border-b">
          <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
            <h4 class="tw-text-sm tw-font-semibold">
              {{ t('customer_management.assign_team.assign_purchaser') }}
            </h4>
            <UserDropdownSelect
              :users="availablePurchasers"
              :placeholder="t('customer_management.assign_team.add_user')"
              @select="userId => handleAddUser('purchasers', userId)"
            />
          </div>

          <div class="tw-min-h-[60px] tw-bg-gray-50 tw-rounded-lg tw-p-4">
            <div v-if="data.team.purchasers.length > 0" class="tw-flex tw-flex-wrap tw-gap-2">
              <Badge
                v-for="userId in data.team.purchasers"
                :key="userId"
                variant="secondary"
                class="tw-gap-1 tw-bg-white tw-border"
              >
                {{ getUserName(userId) }}
                <button
                  type="button"
                  @click="handleRemoveUser('purchasers', userId)"
                  class="tw-ml-1 hover:tw-bg-destructive/20 tw-rounded-sm tw-p-0.5"
                >
                  <X class="tw-w-3 tw-h-3" />
                </button>
              </Badge>
            </div>
            <p v-else class="tw-text-sm tw-text-gray-500">
              {{ t('customer_management.assign_team.no_users_assigned') }}
            </p>
          </div>
        </div>

        <!-- Order Clearing Groups Section -->
        <div class="tw-py-6 tw-border-b">
          <ClearingGroupsSection
            :groups="data.team.orderClearingGroups"
            :all-users="baseAvailableUsers"
            :title="t('customer_management.assign_team.order_clearing_groups')"
            @update:groups="handleOrderClearingGroupsChange"
          />
        </div>

        <!-- Payers Section -->
        <div class="tw-py-6 tw-border-b">
          <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
            <h4 class="tw-text-sm tw-font-semibold">
              {{ t('customer_management.assign_team.assign_payer') }}
            </h4>
            <UserDropdownSelect
              :users="availablePayers"
              :placeholder="t('customer_management.assign_team.add_user')"
              @select="userId => handleAddUser('payers', userId)"
            />
          </div>

          <div class="tw-min-h-[60px] tw-bg-gray-50 tw-rounded-lg tw-p-4">
            <div v-if="data.team.payers.length > 0" class="tw-flex tw-flex-wrap tw-gap-2">
              <Badge
                v-for="userId in data.team.payers"
                :key="userId"
                variant="secondary"
                class="tw-gap-1 tw-bg-white tw-border"
              >
                {{ getUserName(userId) }}
                <button
                  type="button"
                  @click="handleRemoveUser('payers', userId)"
                  class="tw-ml-1 hover:tw-bg-destructive/20 tw-rounded-sm tw-p-0.5"
                >
                  <X class="tw-w-3 tw-h-3" />
                </button>
              </Badge>
            </div>
            <p v-else class="tw-text-sm tw-text-gray-500">
              {{ t('customer_management.assign_team.no_users_assigned') }}
            </p>
          </div>
        </div>

        <!-- Payment Clearing Groups Section -->
        <div class="tw-py-6 tw-border-b">
          <ClearingGroupsSection
            :groups="data.team.paymentClearingGroups"
            :all-users="baseAvailableUsers"
            :title="t('customer_management.assign_team.payment_clearing_groups')"
            @update:groups="handlePaymentClearingGroupsChange"
          />
        </div>

        <!-- Enhanced Approvers Section -->
        <div class="tw-py-6 tw-border-b">
          <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
            <div class="tw-flex tw-items-center tw-gap-2">
              <h4 class="tw-text-sm tw-font-semibold tw-m-0">
                {{ t('customer_management.assign_team.assign_approver') }}
              </h4>
              <Badge
                variant="outline"
                class="tw-gap-1 tw-text-xs tw-bg-yellow-50 tw-border-yellow-200 tw-text-yellow-700"
              >
                <AlertTriangle class="tw-w-3 tw-h-3" />
                <span class="tw-text-xs tw-m-0">
                  {{ t('customer_management.assign_team.critical_role') }}
                </span>
              </Badge>
            </div>
            <UserDropdownSelect
              :users="availableApprovers"
              :placeholder="t('customer_management.assign_team.add_user')"
              @select="userId => handleAddApprover(userId)"
            />
          </div>

          <div class="tw-min-h-[60px] tw-bg-gray-50 tw-rounded-lg tw-p-4">
            <div v-if="data.team.approvers.length > 0" class="tw-space-y-3">
              <div
                v-for="approver in data.team.approvers"
                :key="approver.userId"
                class="tw-flex tw-items-center tw-justify-between tw-p-3 tw-border tw-rounded-lg tw-bg-white"
              >
                <div class="tw-flex tw-items-center tw-gap-3">
                  <div>
                    <p class="tw-font-medium tw-text-sm">
                      {{ getUserName(approver.userId) }}
                    </p>
                    <p class="tw-text-xs tw-text-gray-500">
                      {{ t('customer_management.assign_team.approver_role') }}
                    </p>
                  </div>
                </div>

                <div class="tw-flex tw-items-center tw-gap-3">
                  <div class="tw-flex tw-items-center tw-gap-2">
                    <span class="tw-text-sm tw-text-gray-600">
                      {{ t('customer_management.assign_team.limit') }}:
                    </span>
                    <ShadcnCurrencyInput
                      :model-value="approver.limit"
                      @update:model-value="v => handleApproverLimitChange(approver.userId, v)"
                      class="tw-w-32"
                    />
                  </div>

                  <button
                    type="button"
                    @click="handleRemoveApprover(approver.userId)"
                    class="tw-p-1 tw-text-gray-400 hover:tw-text-red-500 hover:tw-bg-red-50 tw-rounded"
                  >
                    <X class="tw-w-4 tw-h-4" />
                  </button>
                </div>
              </div>
            </div>
            <p v-else class="tw-text-sm tw-text-gray-500">
              {{ t('customer_management.assign_team.no_users_assigned') }}
            </p>
          </div>
        </div>

        <!-- Viewers Section -->
        <div class="tw-pt-6">
          <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
            <h4 class="tw-text-sm tw-font-semibold">
              {{ t('customer_management.assign_team.assign_viewers') }}
            </h4>
            <UserDropdownSelect
              :users="availableViewers"
              :placeholder="t('customer_management.assign_team.add_user')"
              @select="userId => handleAddUser('viewers', userId)"
            />
          </div>

          <div class="tw-min-h-[60px] tw-bg-gray-50 tw-rounded-lg tw-p-4">
            <div v-if="data.team.viewers.length > 0" class="tw-flex tw-flex-wrap tw-gap-2">
              <Badge
                v-for="userId in data.team.viewers"
                :key="userId"
                variant="secondary"
                class="tw-gap-1 tw-bg-white tw-border"
              >
                {{ getUserName(userId) }}
                <button
                  type="button"
                  @click="handleRemoveUser('viewers', userId)"
                  class="tw-ml-1 hover:tw-bg-destructive/20 tw-rounded-sm tw-p-0.5"
                >
                  <X class="tw-w-3 tw-h-3" />
                </button>
              </Badge>
            </div>
            <p v-else class="tw-text-sm tw-text-gray-500">
              {{ t('customer_management.assign_team.no_users_assigned') }}
            </p>
          </div>
        </div>
      </div>
    </CardContent>

    <UserInviteDialog
      :is-open="showInviteDialog"
      @close="showInviteDialog = false"
      @invite="handleInviteUser"
    />

    <StepFooter
      v-if="!isEdit"
      :current-step="2"
      :total-steps="4"
      :loading="false"
      :on-validate="validateForm"
      @previous="handlePrevious"
      @next="handleNext"
      @finish="handleFinish"
      class="tw-px-6 tw-pb-6"
    />

    <div v-if="isEdit && onSave" class="tw-flex tw-px-6 tw-pb-6 tw-justify-end">
      <SectionSaveButton
        :on-save="onSave"
        :is-edit="isEdit"
        section-name="customer_management.assign_team.title"
      />
    </div>
  </Card>
</template>

<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import ShadcnCurrencyInput from '@/components/ui/currency-input/ShadcnCurrencyInput.vue';
import { useCustomerStore } from '@/stores/customer.store';
import { AlertTriangle, UserPlus, Users, X } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ClearingGroupsSection from './ClearingGroupsSection.vue';
import SectionSaveButton from './SectionSaveButton.vue';
import StepFooter from './StepFooter.vue';
import UserDropdownSelect from './UserDropdownSelect.vue';
import UserInviteDialog from './UserInviteDialog.vue';

const { t } = useI18n();

// Types
interface User {
  id: number | string;
  display_name: string;
  email: string;
  first_name: string;
  last_name: string;
  roles?: string[];
  active: boolean;
  is_admin: boolean;
}

interface ClearingGroup {
  id?: number;
  name: string;
  display_order: number;
  assignees: (number | string)[];
}

interface TeamData {
  purchasers: (string | number)[];
  orderClearingGroups: ClearingGroup[];
  payers: (string | number)[];
  paymentClearingGroups: ClearingGroup[];
  approvers: { userId: string | number; limit: number }[];
  viewers: (string | number)[];
}

type SaveResult = { success: boolean; message: string };

interface TeamAssignSectionProps {
  data: { team: TeamData };
  users: User[];
  isEdit: boolean;
  onSave?: () => SaveResult | Promise<SaveResult>;
}

// Props
const props = defineProps<TeamAssignSectionProps>();

// Emits
const emit = defineEmits<{
  change: [updates: { team: TeamData }];
  inviteUser: [users: User[]];
  previous: [];
  next: [];
  finish: [];
}>();

// Reactive state
const showInviteDialog = ref(false);
const customerStore = useCustomerStore();
const localUsers = ref<User[]>([]);

// Initialize localUsers from parent users and keep in sync
localUsers.value = [...props.users];
watch(
  () => props.users,
  val => {
    localUsers.value = [...val];
  },
  { deep: true }
);

// Computed
const allUsers = computed(() => [...localUsers.value]);

// Base filter for all selections (exclude admins and inactive users)
const baseAvailableUsers = computed(() =>
  allUsers.value.filter(user => !user.is_admin && user.active)
);

const availablePurchasers = computed(() =>
  baseAvailableUsers.value.filter(user => !props.data.team.purchasers.includes(user.id))
);

const availablePayers = computed(() =>
  baseAvailableUsers.value.filter(user => !props.data.team.payers.includes(user.id))
);

const availableApprovers = computed(() =>
  baseAvailableUsers.value.filter(
    user => !props.data.team.approvers.find(a => a.userId === user.id)
  )
);

const availableViewers = computed(() =>
  baseAvailableUsers.value.filter(user => !props.data.team.viewers.includes(user.id))
);

// Methods
const getUserName = (userId: string | number) => {
  const user = allUsers.value.find(u => u.id === userId);
  return user ? user.display_name : t('customer_management.assign_team.unknown_user');
};

// Type for simple user ID array fields (excludes approvers and clearing groups)
type SimpleUserArrayKey = 'purchasers' | 'payers' | 'viewers';

const handleAddUser = (role: string, userId: string | number) => {
  const updatedTeam = { ...props.data.team };
  const roleKey = role as SimpleUserArrayKey;
  if (!updatedTeam[roleKey].includes(userId)) {
    updatedTeam[roleKey] = [...updatedTeam[roleKey], userId];
  }
  emit('change', { team: updatedTeam });
};

const handleRemoveUser = (role: string, userId: string | number) => {
  const updatedTeam = { ...props.data.team };
  const roleKey = role as SimpleUserArrayKey;
  updatedTeam[roleKey] = updatedTeam[roleKey].filter(id => id !== userId);
  emit('change', { team: updatedTeam });
};

const handleAddApprover = (userId: string | number) => {
  const updatedTeam = { ...props.data.team };
  if (!updatedTeam.approvers.find(a => a.userId === userId)) {
    updatedTeam.approvers = [
      ...updatedTeam.approvers,
      { userId, limit: 0 }, // Default limit of 0, user can edit it
    ];
    emit('change', { team: updatedTeam });
  }
};

const handleRemoveApprover = (userId: string | number) => {
  const updatedTeam = { ...props.data.team };
  updatedTeam.approvers = updatedTeam.approvers.filter(a => a.userId !== userId);
  emit('change', { team: updatedTeam });
};

const handleApproverLimitChange = (userId: string | number, value: string | number | null) => {
  const limit = typeof value === 'string' ? parseFloat(value) : value || 0;
  const updatedTeam = { ...props.data.team };
  updatedTeam.approvers = updatedTeam.approvers.map(a =>
    a.userId === userId ? { ...a, limit: isNaN(limit) ? 0 : limit } : a
  );
  emit('change', { team: updatedTeam });
};

// Clearing group handlers
const handleOrderClearingGroupsChange = (groups: ClearingGroup[]) => {
  const updatedTeam = { ...props.data.team, orderClearingGroups: groups };
  emit('change', { team: updatedTeam });
};

const handlePaymentClearingGroupsChange = (groups: ClearingGroup[]) => {
  const updatedTeam = { ...props.data.team, paymentClearingGroups: groups };
  emit('change', { team: updatedTeam });
};

// no draft/blur logic; behave like MainDataForm currency inputs

// Validation function for StepFooter
const validateForm = (): { isValid: boolean; errors: string[] } => {
  // For team assignment, we just check basic requirements
  // Most validation is handled by the form itself
  // const isValid = props.data.team.approvers.length > 0;

  return {
    isValid: true,
    errors: [], // We don't need to return errors since this will be handled by form UI
  };
};

const handlePrevious = () => {
  emit('previous');
};

const handleNext = () => {
  emit('next');
};

const handleFinish = () => {
  emit('finish');
};

// Maps dialog role ids to API roles and team keys
// Note: order-clearer and payment-clearer are now handled via clearing groups
const roleMap: Record<string, { api: string; teamKey: keyof TeamData }> = {
  purchaser: { api: 'PURCHASER', teamKey: 'purchasers' },
  payer: { api: 'PAYER', teamKey: 'payers' },
  approver: { api: 'APPROVER', teamKey: 'approvers' as any },
  viewer: { api: 'VIEWER', teamKey: 'viewers' },
};

type InviteDialogUser = {
  id: string | number;
  firstName: string;
  lastName: string;
  email: string;
  roles?: string[];
  approverLimit?: number;
};

const refreshUsers = async () => {
  const users = await customerStore.getUsers();
  localUsers.value = users;
};

const handleInviteUser = async (user: InviteDialogUser) => {
  try {
    // Build payload for API
    const payload: any = {
      first_name: user.firstName,
      last_name: user.lastName,
      display_name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      // Do not send roles here; assignment handled on save step
    };

    await customerStore.addUser(payload);

    // Refresh users list
    await refreshUsers();

    // Find the newly added user by email
    const created = allUsers.value.find(u => u.email === user.email);
    if (!created) {
      showInviteDialog.value = false;
      return;
    }

    // Add to selected roles in the form
    const updatedTeam = { ...props.data.team };
    (user.roles || []).forEach(r => {
      const mapping = roleMap[r];
      if (!mapping) return;

      if (mapping.teamKey === 'approvers') {
        const exists = updatedTeam.approvers.find(a => a.userId === created.id);
        if (!exists) {
          updatedTeam.approvers = [
            ...updatedTeam.approvers,
            { userId: created.id, limit: user.approverLimit || 0 },
          ];
        }
      } else {
        const key = mapping.teamKey as keyof Omit<TeamData, 'approvers'>;
        const list = updatedTeam[key] as (string | number)[];
        if (!list.includes(created.id)) {
          updatedTeam[key] = [...list, created.id] as any;
        }
      }
    });

    emit('change', { team: updatedTeam });
    showInviteDialog.value = false;
  } catch (e) {
    // Keep dialog open to allow user to retry
    console.error(e);
  }
};
</script>
